import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  EMAIL_RESEND_2FA_CODE_SUCCESS_MESSAGE,
  SEND_CODE_UNEXPECTED_ERROR_MESSAGE,
  SMS_RESEND_2FA_CODE_SUCCESS_MESSAGE,
} from "common/constants/copy";
import { TwoFaType as TwoFaTypeEnum } from "common/types/mainEnums";
import { useApolloClient } from "@apollo/client";
import { GetSessionStatus } from "apps/portal/Portal/common/queries.gql";
import {
  type GetSessionStatusQueryResult,
  useGetAvailableTwoStepAuthMethodsQuery,
  useGetSubscriptionRedirectUrlLazyQuery,
  useGetSubscriptionRedirectUrlQuery,
  useTwoStepAuthSendTokenToEmailForConfigurationMutation,
  useTwoStepAuthSendTokenToSmsForConfigurationMutation,
} from "common/types/mainHooks";
import {
  type TwoFaType,
  type TwoStepAuthSendTokenToEmailForConfigurationMutation,
  type TwoStepAuthSendTokenToSmsForConfigurationMutation,
} from "common/types/mainTypes";
import { toast } from "@jsq/ds/core";
import { isEmpty, omit } from "lodash";
import {
  useGetAccountContactsQuery,
  useGetEmailDistributionListQuery,
} from "common/types/federation/supergraphHooks";
import {
  type AccountContactsEdge,
  type EmailDistributionListObjectType,
  type GetEmailDistributionListQuery,
} from "common/types/federation/supergraphTypes";
import { federationGatewayClient } from "common/data/graphql/federation-gateway-client";
import {
  type SendCodeArgs,
  type SendEmailDetails,
  type SendSmsDetails,
  type SendTwoFaCodeByTypeArgs,
  type SendTwoFaCodeByTypeHookResult,
} from "./AccountActivation/types";
import { type Control2FAMethodSelection } from "./types";
import { getGroupedContacts } from "./Settings/InvestmentAccounts/utils";
import { type GroupedContact } from "./Settings/InvestmentAccounts/types";
import { type User } from "./AppContext";

/**
 * A custom hook to control the state of which 2FA method to use.
 * If twoFaMethodOverride is provided, it will be used instead of querying for available methods.
 */
export const useControl2FAMethodSelection = (
  initial2FAMethod: TwoFaTypeEnum = TwoFaTypeEnum.Sms,
  initial2FAMethodSelection = true,
  twoFaMethodOverride?: TwoFaType[],
): Control2FAMethodSelection => {
  const [show2FAMethod, setShow2FAMethod] = useState<TwoFaTypeEnum>(initial2FAMethod);
  const [show2FAMethodSelection, setShow2FAMethodSelection] = useState(
    initial2FAMethodSelection,
  );
  const { data, loading: isLoading } = useGetAvailableTwoStepAuthMethodsQuery({
    skip: !!twoFaMethodOverride,
  });

  const availableTwoFATypes =
    twoFaMethodOverride || data?.currentGlobalUser?.availableTwoStepAuthMethods;

  const show2FAMethodCallback = (method: TwoFaTypeEnum) =>
    !!availableTwoFATypes && availableTwoFATypes.includes(method)
      ? () => setShow2FAMethod(method)
      : undefined;

  return {
    current2FAMethod: show2FAMethod,
    showEmail2FAMethod: show2FAMethodCallback(TwoFaTypeEnum.Email),
    showSMS2FAMethod: show2FAMethodCallback(TwoFaTypeEnum.Sms),
    showTotp2FAMethod: show2FAMethodCallback(TwoFaTypeEnum.Totp),
    isShowing2FAMethodSelection: show2FAMethodSelection,
    show2FAMethodSelection: setShow2FAMethodSelection,
    isLoading: twoFaMethodOverride ? false : isLoading,
  };
};

/**
 * Hook to call either SMS or Email Sending Mutations depending on 2FA type and input.
 * Input can either be provided in advance, or updated on consecutive calls.
 */
export const useSendTwoFaCodeByType = (
  sendTwoFaCodeByTypeArgs: SendTwoFaCodeByTypeArgs = {},
): SendTwoFaCodeByTypeHookResult => {
  const sendOnInitialRender = sendTwoFaCodeByTypeArgs.sendOnInitialRender || false;
  const [hasSentOnInitialRender, setHasSentOnInitialRender] = useState<boolean>(false);

  const initialTwoFaDetails = omit(sendTwoFaCodeByTypeArgs, ["sendOnInitialRender"]) as
    | SendSmsDetails
    | SendEmailDetails;
  const [twoFaDetails, setTwoFaDetails] = useState<SendSmsDetails | SendEmailDetails | null>(
    !isEmpty(initialTwoFaDetails) ? initialTwoFaDetails : null,
  );

  const [twoStepAuthSendTokenToSmsForConfiguration] =
    useTwoStepAuthSendTokenToSmsForConfigurationMutation({
      onCompleted: (data) => {
        if (
          data?.twoStepAuthSendTokenToSmsForConfiguration?.__typename ===
          "MutationErrorResponse"
        ) {
          toast.error(data.twoStepAuthSendTokenToSmsForConfiguration.message);
        }
      },
      onError: () => {
        toast.error(SEND_CODE_UNEXPECTED_ERROR_MESSAGE);
      },
    });
  const [twoStepAuthSendTokenToEmailForConfiguration] =
    useTwoStepAuthSendTokenToEmailForConfigurationMutation({
      onCompleted: (data) => {
        if (
          data?.twoStepAuthSendTokenToEmailForConfiguration?.__typename ===
          "MutationErrorResponse"
        ) {
          toast.error(data.twoStepAuthSendTokenToEmailForConfiguration.message);
        }
      },
      onError: () => {
        toast.error(SEND_CODE_UNEXPECTED_ERROR_MESSAGE);
      },
    });

  const sendCode = useCallback(
    async (isResend = false, _twoFaDetails: SendCodeArgs = {}) => {
      if (!isEmpty(_twoFaDetails)) {
        setTwoFaDetails(_twoFaDetails as SendSmsDetails | SendEmailDetails);
      }
      const localTwoFaDetails = !isEmpty(_twoFaDetails) ? _twoFaDetails : twoFaDetails;

      let result;
      if (localTwoFaDetails && !isEmpty(localTwoFaDetails)) {
        if (localTwoFaDetails.twoFaType === TwoFaTypeEnum.Sms) {
          result = await twoStepAuthSendTokenToSmsForConfiguration({
            variables: {
              objectInput: {
                phoneNumber: localTwoFaDetails.phoneNumber,
                countryCode: localTwoFaDetails.countryCode,
              },
            },
            context: { unauthenticated: true },
          });
        } else if (localTwoFaDetails.twoFaType === TwoFaTypeEnum.Email) {
          result = await twoStepAuthSendTokenToEmailForConfiguration({
            variables: { objectInput: {} },
            context: { unauthenticated: true },
          });
        }

        // If the send operation is a resend then pop a toast to notify the user on success.
        if (
          isResend &&
          ((result?.data as TwoStepAuthSendTokenToSmsForConfigurationMutation)
            ?.twoStepAuthSendTokenToSmsForConfiguration?.__typename ===
            "MutationSuccessResponse" ||
            (result?.data as TwoStepAuthSendTokenToEmailForConfigurationMutation)
              ?.twoStepAuthSendTokenToEmailForConfiguration?.__typename ===
              "MutationSuccessResponse")
        ) {
          if (localTwoFaDetails.twoFaType === TwoFaTypeEnum.Sms) {
            toast.info(SMS_RESEND_2FA_CODE_SUCCESS_MESSAGE, { position: "bottom-center" });
          } else if (localTwoFaDetails.twoFaType === TwoFaTypeEnum.Email) {
            toast.info(EMAIL_RESEND_2FA_CODE_SUCCESS_MESSAGE, { position: "bottom-center" });
          }
        }
      }
    },
    [
      twoFaDetails,
      twoStepAuthSendTokenToSmsForConfiguration,
      twoStepAuthSendTokenToEmailForConfiguration,
    ],
  );

  /**
   * Send a code to the users pending-configuration 2FA device.
   */

  useEffect(() => {
    if (sendOnInitialRender && !hasSentOnInitialRender && !isEmpty(twoFaDetails)) {
      sendCode();
      setHasSentOnInitialRender(true);
    }
  }, [sendCode, sendOnInitialRender, hasSentOnInitialRender, twoFaDetails]);

  return [sendCode, twoFaDetails];
};

export const useSessionCache = () => {
  const client = useApolloClient();
  return client.readQuery<GetSessionStatusQueryResult["data"]>({
    query: GetSessionStatus,
  });
};

/**
 * A custom hook to countdown from a given number of seconds and exposes a reset function.
 */
export const useCountdown = (countdownDurationInSeconds: number) => {
  const getFinishTime = () => Date.now() + countdownDurationInSeconds * 1000;
  const [finishTime, setFinishTime] = useState(getFinishTime());
  const [currentTime, setCurrentTime] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      if (currentTime < finishTime) {
        setCurrentTime(Date.now());
      }
    }, 100);
    return () => clearInterval(interval);
  }, [currentTime, finishTime]);

  const resetCountdown = () => {
    setFinishTime(getFinishTime());
    setCurrentTime(Date.now());
  };

  // Round up the time remaining to the nearest second.
  const timeRemaining = Math.ceil((finishTime - currentTime) / 1000);

  return {
    resetCountdown,
    timeRemaining,
    isExpired: timeRemaining <= 0,
  };
};

export const useGetGroupedAccountContacts = (
  accountIds: string[],
  arenaIds: string[],
  user?: User,
  nameOrEmail?: string,
  skipValue?: boolean,
) => {
  const [groupedContacts, setGroupedContacts] = useState<GroupedContact[]>([]);
  const { loading } = useGetAccountContactsQuery({
    variables: {
      filters: {
        accountId: accountIds,
        arena: arenaIds,
        nameOrEmail: nameOrEmail || null,
      },
      first: 100000,
    },
    client: federationGatewayClient,
    fetchPolicy: "no-cache",
    skip: !!skipValue,
    onCompleted: (data) => {
      const groupedContacts = getGroupedContacts(
        data?.accountContacts?.edges as AccountContactsEdge[],
        user?.emailAddress,
      );
      setGroupedContacts(groupedContacts);
    },
    onError: () => {
      toast.error("Could not process your request. Please refresh and try again.", {
        position: "bottom-center",
      });
    },
  });

  return { loading, groupedContacts };
};

export const useGetEmailDistributionsList = (
  arenaId: string,
  investorCanManage: boolean,
  maintainSortOrder?: boolean,
) => {
  const [emailDistributionList, setEmailDistributionList] = React.useState<
    EmailDistributionListObjectType[]
  >([]);
  const { loading } = useGetEmailDistributionListQuery({
    variables: {
      arenaId,
      investorCanManage,
    },
    client: federationGatewayClient,
    skip: !arenaId,
    fetchPolicy: "cache-and-network",
    onCompleted: (data: GetEmailDistributionListQuery) => {
      const distributionList = [...data.emailDistributionList];
      if (maintainSortOrder) {
        distributionList.sort((a, b) => a.sortOrder - b.sortOrder);
      }
      setEmailDistributionList(distributionList as EmailDistributionListObjectType[]);
    },
  });

  return { loading, emailDistributionList };
};

type GetSubscriptionUrlVariablesArg = {
  arenaDomain: string;
  dataRoomKey: string;
};

export const useGetSubscriptionUrlLazy = () => {
  const [getSubscriptionRedirectUrl] = useGetSubscriptionRedirectUrlLazyQuery();

  return (variables: GetSubscriptionUrlVariablesArg) => {
    return getSubscriptionRedirectUrl({
      context: { unauthenticated: true },
      variables,
    });
  };
};

interface GetSubscriptionUrlConfig extends GetSubscriptionUrlVariablesArg {
  skip?: boolean;
}

export const useGetSubscriptionUrl = ({
  arenaDomain,
  dataRoomKey,
  skip = false,
}: GetSubscriptionUrlConfig) => {
  const location = useLocation();
  const pathAfterDataRoomKey = location.pathname?.split(dataRoomKey)?.[1] || "";
  const stagePath = `${pathAfterDataRoomKey}${location.search}`;

  return useGetSubscriptionRedirectUrlQuery({
    variables: {
      arenaDomain,
      dataRoomKey,
      stagePath,
    },
    context: { unauthenticated: true },
    skip,
  });
};
